import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUSES = ["pending", "approved", "rejected", "all"];

export default function AdminActivations() {
  const { t, lang } = useLang();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/activation-requests", { params: { status } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/activation-requests/${id}/approve`);
      toast.success(t("activation_approved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/admin/activation-requests/${id}/reject`);
      toast.success(t("activation_rejected"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusLabels = {
    pending: t("filter_pending"),
    approved: t("filter_approved"),
    rejected: t("filter_rejected"),
    all: t("filter_all"),
  };

  return (
    <div className="space-y-4" data-testid="admin-activations-page">
      <h1 className="text-2xl sm:text-3xl font-semibold">{t("activation_requests")}</h1>

      <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl w-fit">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`act-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-2 text-sm rounded-lg ${status === s ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
          >
            {statusLabels[s]}
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-8 text-center text-[#5C6053] text-sm">—</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r) => (
          <div key={r.id} data-testid={`activation-card-${r.id}`} className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold">{r.owner_name || "—"}</h3>
                  <StatusBadge status={r.status} t={t} />
                </div>
                <div className="text-xs text-[#5C6053] mt-1" dir="ltr">{r.owner_phone}</div>
                <div className="text-xs text-[#5C6053]">{r.owner_email}</div>
              </div>
              <div className="text-end">
                <div className="text-lg font-semibold">{r.amount} <span className="text-xs text-[#5C6053]">{(r.currency || "omr").toUpperCase()}</span></div>
                <div className="text-xs text-[#5C6053]">{r.package_id === "premium" ? t("plan_premium") : t("plan_standard")}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-[#5C6053]">
              {r.created_at?.slice(0, 16).replace("T", " ")}
            </div>
            {r.status === "pending" && (
              <div className="mt-4 flex gap-2 justify-end">
                <button data-testid={`act-reject-${r.id}`} onClick={() => reject(r.id)} className="px-3 py-1.5 text-xs rounded-lg border border-[#C96A52] text-[#C96A52] inline-flex items-center gap-1">
                  <XCircle size={14} /> {t("reject")}
                </button>
                <button data-testid={`act-approve-${r.id}`} onClick={() => approve(r.id)} className="px-3 py-1.5 text-xs rounded-lg brand-btn inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> {t("approve")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: { cls: "bg-[#FFF3D6] text-[#8A6D1F]", icon: Clock, label: t("filter_pending") },
    approved: { cls: "bg-[#E6F1E7] text-[#2A5948]", icon: CheckCircle2, label: t("filter_approved") },
    rejected: { cls: "bg-[#FBE9E4] text-[#A85540]", icon: XCircle, label: t("filter_rejected") },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded ${s.cls}`}>
      <Icon size={10} />
      {s.label}
    </span>
  );
}
